import { useState, useEffect } from 'react';
import SEOHead from '../../components/SEOHead';
import PopupBanner from '../../components/PopupBanner';

export default function LPAltPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formType, setFormType] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = formData.get('email') as string;
    
    // Check if email is already registered in sessionStorage
    const registeredEmails = JSON.parse(sessionStorage.getItem('registeredContactEmails') || '[]');
    if (registeredEmails.includes(email)) {
      alert('このメールアドレスは既に登録されています。');
      return;
    }
    
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      alert('メッセージは500文字以内で入力してください');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d4n8o61btbscp07qbai0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        // Save email to sessionStorage to prevent duplicate registration
        registeredEmails.push(email);
        sessionStorage.setItem('registeredContactEmails', JSON.stringify(registeredEmails));
        
        setFormType('contact');
        setShowSuccess(true);
        form.reset();
        setTimeout(() => setShowSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleMobileMenuClick = (targetId: string) => {
    setIsMobileMenuOpen(false);
    
    setTimeout(() => {
      const element = document.querySelector(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const toggleMobileMenu = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMobileMenuOpen(prev => !prev);
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        "name": "株式会社マック",
        "url": siteUrl,
        "description": "系統用蓄電池事業希望者向けのサポート。東京電力管内に特化し、月間最大4,000万円の収益実績。",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+81-48-729-7222",
          "contactType": "customer service",
          "email": "info@eins-ltd.com",
          "availableLanguage": "Japanese"
        }
      },
      {
        "@type": "WebPage",
        "@id": `${siteUrl}/lp-alt#webpage`,
        "url": `${siteUrl}/lp-alt`,
        "name": "成長市場に乗る！系統用蓄電池事業のご案内 | 1,900万円～の共有型ビジネスモデル",
        "description": "今、注目の次世代エネルギー投資。共有型蓄電池なら1,000万円台から参入でき、高利回り・長期安定収益が期待できます。系統用蓄電池市場の拡大とともに成長する新たな投資機会。",
        "inLanguage": "ja",
        "isPartOf": {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          "url": siteUrl,
          "name": "株式会社マック - 系統用蓄電池事業のご案内"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "収益分配はどのように行われますか?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "蓄電所全体の市場収益からアグリゲーター手数料・分配手数料・各経費を控除し、管理会社から共有持分割合に応じて収益が分配されます。"
            }
          },
          {
            "@type": "Question",
            "name": "リスク管理はどのように実施されますか?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "自然災害のリスクを最小限に抑えるため、ハザードマップでリスクの低いエリアを選定して用地をご提案しています。さらに、万が一の災害に備えて損害補償・休業補償に対応した保険プランにも加入しております。また、設置する設備にはメーカーの製品保証も付帯しており、安心してご利用いただけます。"
            }
          },
          {
            "@type": "Question",
            "name": "メンテナンスや運用管理はどのように行われますか?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "定期点検は、蓄電池メーカーおよび施工業者が行い、法定点検に関しては外部の主任技術者に委託します。"
            }
          },
          {
            "@type": "Question",
            "name": "蓄電池の寿命はどのくらいですか?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "一般的に蓄電池の寿命は約15~20年とされています。充放電回数や設置エリアの気候により多少左右されることがあります。"
            }
          },
          {
            "@type": "Question",
            "name": "契約の途中で売却することは可能ですか?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "事業譲渡は可能ですが、アグリゲーターやメーカー保証の契約期間中の場合、事前承諾が必要となります。また、将来的な中古市場の動向については現時点では予測が難しい状況です。"
            }
          }
        ]
      },
      {
        "@type": "Service",
        "serviceType": "系統用蓄電池事業",
        "provider": {
          "@id": `${siteUrl}/#organization`
        },
        "areaServed": {
          "@type": "State",
          "name": "東京電力管内"
        },
        "description": "共有型蓄電池投資モデル。1,900万円から参入可能で、高利回り・長期安定収益を実現。"
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="系統用蓄電池事業のご案内 | 株式会社マック - 東京電力管内専門"
        description="株式会社マックは系統用蓄電池事業希望者向けのサポートを提供します。東京電力管内に特化し、月間最大4,000万円の収益実績。10年以上の業界経験と81箇所の申請案件で安心の投資をサポートします。"
        keywords="系統用蓄電池,蓄電池投資,東京電力管内,グリッドビヨンド,エネルギー投資"
        canonical={`${siteUrl}/lp-alt`}
        schema={schema}
      />

      <div className="min-h-screen bg-white">
        {/* Success Message */}
        {showSuccess && (
          <div className="fixed top-24 right-6 bg-teal-600 text-white px-6 py-4 rounded-lg shadow-lg z-50 animate-fade-in">
            <div className="flex items-center">
              <i className="ri-check-line text-2xl mr-3"></i>
              <span className="font-semibold">
                {formType === 'contact' ? 'お問い合わせを受け付けました' : '資料請求を受け付けました'}
              </span>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <div className="flex items-center">
                <div className="flex items-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg mr-3">
                    <i className="ri-battery-charge-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-gray-900">株式会社マック</div>
                    <div className="text-xs text-gray-600">系統用蓄電池事業のご案内</div>
                  </div>
                </div>
              </div>
              <div className="hidden md:flex items-center space-x-8">
                <a href="#recommend" className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">おすすめ</a>
                <a href="#market" className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">市場動向</a>
                <a href="#reasons" className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">選ばれる理由</a>
                <a href="#simulation" className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">シミュレーション</a>
                <a href="#faq" className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">よくある質問</a>
                <a href="#contact" className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer">
                  お問い合わせ
                </a>
              </div>
              <div 
                className="md:hidden cursor-pointer p-3 -mr-2 active:bg-gray-100 rounded-lg transition-colors"
                onClick={toggleMobileMenu}
                onTouchEnd={toggleMobileMenu}
                role="button"
                aria-label="メニュー"
                aria-expanded={isMobileMenuOpen}
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-3xl text-gray-900`}></i>
              </div>
            </div>
          </div>

          {/* Mobile Menu */}
          <div 
            className={`md:hidden bg-white border-t border-gray-200 shadow-lg transition-all duration-300 overflow-hidden ${
              isMobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-6 py-4 space-y-3">
              <div 
                onClick={() => handleMobileMenuClick('#recommend')}
                onTouchEnd={() => handleMobileMenuClick('#recommend')}
                className="block w-full text-left text-base font-medium text-gray-900 hover:text-teal-600 active:text-teal-700 transition-colors py-3 cursor-pointer border-b border-gray-100"
                role="button"
              >
                おすすめ
              </div>
              <div 
                onClick={() => handleMobileMenuClick('#market')}
                onTouchEnd={() => handleMobileMenuClick('#market')}
                className="block w-full text-left text-base font-medium text-gray-900 hover:text-teal-600 active:text-teal-700 transition-colors py-3 cursor-pointer border-b border-gray-100"
                role="button"
              >
                市場動向
              </div>
              <div 
                onClick={() => handleMobileMenuClick('#reasons')}
                onTouchEnd={() => handleMobileMenuClick('#reasons')}
                className="block w-full text-left text-base font-medium text-gray-900 hover:text-teal-600 active:text-teal-700 transition-colors py-3 cursor-pointer border-b border-gray-100"
                role="button"
              >
                選ばれる理由
              </div>
              <div 
                onClick={() => handleMobileMenuClick('#simulation')}
                onTouchEnd={() => handleMobileMenuClick('#simulation')}
                className="block w-full text-left text-base font-medium text-gray-900 hover:text-teal-600 active:text-teal-700 transition-colors py-3 cursor-pointer border-b border-gray-100"
                role="button"
              >
                シミュレーション
              </div>
              <div 
                onClick={() => handleMobileMenuClick('#faq')}
                onTouchEnd={() => handleMobileMenuClick('#faq')}
                className="block w-full text-left text-base font-medium text-gray-900 hover:text-teal-600 active:text-teal-700 transition-colors py-3 cursor-pointer border-b border-gray-100"
                role="button"
              >
                よくある質問
              </div>
              <div 
                onClick={() => handleMobileMenuClick('#contact')}
                onTouchEnd={() => handleMobileMenuClick('#contact')}
                className="block w-full text-left text-base font-medium text-gray-900 hover:text-teal-600 active:text-teal-700 transition-colors py-3 cursor-pointer"
                role="button"
              >
                お問い合わせ
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-20 pb-12 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-8">
              <img 
                src="https://grid-scale-battery.eins-ltd.com/hs-fs/hubfs/EINS%20INC%20logo.png?width=114&height=32&name=EINS%20INC%20logo.png"
                alt="系統用蓄電池投資事業"
                className="w-full h-auto max-w-5xl mx-auto"
              />
            </div>

            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                ビジネスモデルを今すぐ確認!
              </h1>
              <p className="text-base text-gray-600 mb-6">
                \ビジネスモデルを今すぐ確認/
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
              <a 
                href="#contact"
                className="block bg-amber-500 hover:bg-amber-600 text-white text-center px-8 py-4 rounded-lg text-base font-bold transition-all whitespace-nowrap cursor-pointer"
              >
                まずは資料を受取る▷
              </a>
              <a 
                href="#contact"
                className="block bg-teal-600 hover:bg-teal-700 text-white text-center px-8 py-4 rounded-lg text-base font-bold transition-all whitespace-nowrap cursor-pointer"
              >
                無料オンライン相談をする▷
              </a>
            </div>

            <p className="text-center text-sm text-gray-600">
              ※本ページは法人のお客様を対象としたサービスのご案内です
            </p>
          </div>
        </section>

        {/* SDGs Badge */}
        <div className="py-4 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <img 
              src="https://grid-scale-battery.eins-ltd.com/hubfs/%E3%83%91%E3%83%BC%E3%83%88%E3%83%8A%E3%83%BC%E3%83%AD%E3%82%B4_%E3%83%A8%E3%82%B3_%E6%97%A5_color.png"
              alt="埼玉県SDGsパートナー"
              className="h-16 mx-auto"
            />
          </div>
        </div>

        {/* Recommended Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                \こんな方におすすめです/
              </h2>
            </div>

            <div className="max-w-2xl mx-auto mb-12">
              <img 
                src="https://grid-scale-battery.eins-ltd.com/hs-fs/hubfs/%E6%A0%AA%E5%BC%8F%E4%BC%9A%E7%A4%BEEINS%20%E7%B3%BB%E7%B5%B1%E7%94%A8%E8%93%84%E9%9B%BB%E6%B1%A0%E4%BA%8B%E6%A5%AD%20%E6%96%B0LP%20%E3%81%93%E3%82%93%E3%81%AA%E6%96%B9%E3%81%AB%E3%81%8A%E3%81%99%E3%81%99%E3%82%81%E3%81%A7%E3%81%99-3.png"
                alt="こんな方におすすめです"
                className="w-full h-auto"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl shadow-lg text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-4">CASE 1</h3>
                <p className="text-base font-semibold text-gray-800 mb-2">成長市場で</p>
                <p className="text-base font-semibold text-gray-800">利益を得たい方</p>
              </div>
              <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-2xl shadow-lg text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-4">CASE 2</h3>
                <p className="text-base font-semibold text-gray-800 mb-2">収益性と環境貢献を</p>
                <p className="text-base font-semibold text-gray-800">両立したい方</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-2xl shadow-lg text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-4">CASE 3</h3>
                <p className="text-base font-semibold text-gray-800 mb-2">持続可能な社会に</p>
                <p className="text-base font-semibold text-gray-800">貢献したい方</p>
              </div>
            </div>
          </div>
        </section>

        {/* Market Growth Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                今、蓄電池市場が注目される理由とは
              </h2>
              <p className="text-base text-gray-600 mb-2">—————</p>
              <p className="text-base text-gray-600">再エネの急速な普及により出力抑制が深刻化</p>
              <p className="text-base text-gray-600">その解決策として"電気を貯める"力が注目されています</p>
            </div>

            <div className="max-w-4xl mx-auto mb-12">
              <img 
                src="https://grid-scale-battery.eins-ltd.com/hs-fs/hubfs/%E6%A0%AA%E5%BC%8F%E4%BC%9A%E7%A4%BEEINS%20%E7%B3%BB%E7%B5%B1%E7%94%A8%E8%93%84%E9%9B%BB%E6%B1%A0%E4%BA%8B%E6%A5%AD%20%E6%96%B0LP%20%E8%93%84%E9%9B%BB%E6%B1%A0%E4%B8%96%E7%95%8C%E5%B8%82%E5%A0%B4%E6%8E%A8%E7%A7%BB%E3%82%B0%E3%83%A9%E3%83%95-1.png"
                alt="蓄電池世界市場推移グラフ"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>

            <div className="text-center max-w-3xl mx-auto">
              <p className="text-base text-gray-700 mb-4">
                調整力確保の要として蓄電池市場は世界的に急成長中
              </p>
              <p className="text-base text-gray-700 mb-4">
                系統の空き容量争いが全国で加速
              </p>
              <p className="text-base font-bold text-teal-600">
                今がまさに参入の好機です
              </p>
            </div>
          </div>
        </section>

        {/* Revenue Model Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                収益モデルの魅力
              </h2>
              <p className="text-base text-gray-600 mb-2">—————</p>
              <p className="text-base text-gray-600">"貯めて売る"だけじゃない<br />3つの市場を活用し 収益を広げます</p>
            </div>

            <div className="max-w-4xl mx-auto mb-12">
              <img 
                src="https://grid-scale-battery.eins-ltd.com/hubfs/%E6%A0%AA%E5%BC%8F%E4%BC%9A%E7%A4%BEEINS%20%E7%B3%BB%E7%B5%B1%E7%94%A8%E8%93%84%E9%9B%BB%E6%B1%A0%E4%BA%8B%E6%A5%AD%20%E6%96%B0LP%20%E8%B2%AF%E3%82%81%E3%81%A6%E5%A3%B2%E3%82%8B.png"
                alt="貯めて売る収益モデル"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>

            <div className="text-center max-w-3xl mx-auto mb-12">
              <p className="text-base text-gray-700 mb-4">
                安い時間帯に蓄電し、電力価格が高い夜間に売電<br />
                時間帯ごとの価格差を活用した基本の収益モデルです
              </p>
              <p className="text-base font-bold text-gray-900 mb-2">
                "貯めて売る"だけでは終わらない
              </p>
              <p className="text-base font-bold text-gray-900">
                3つの市場でさらなる利益も
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
              <div className="text-center">
                <div className="w-32 h-32 flex items-center justify-center mx-auto mb-4">
                  <img 
                    src="https://grid-scale-battery.eins-ltd.com/hubfs/%E6%A0%AA%E5%BC%8F%E4%BC%9A%E7%A4%BEEINS%20%E7%B3%BB%E7%B5%B1%E7%94%A8%E8%93%84%E9%9B%BB%E6%B1%A0%E4%BA%8B%E6%A5%AD%20%E6%96%B0LP%20%E5%AE%B9%E9%87%8F%E5%B8%82%E5%A0%B4%20%E3%82%A2%E3%82%A4%E3%82%B3%E3%83%B3%20.png"
                    alt="容量市場"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-lg font-bold text-gray-900">容量市場</h3>
              </div>
              <div className="text-center">
                <div className="w-32 h-32 flex items-center justify-center mx-auto mb-4">
                  <img 
                    src="https://grid-scale-battery.eins-ltd.com/hubfs/%E6%A0%AA%E5%BC%8F%E4%BC%9A%E7%A4%BEEINS%20%E7%B3%BB%E7%B5%B1%E7%94%A8%E8%93%84%E9%9B%BB%E6%B1%A0%E4%BA%8B%E6%A5%AD%20%E6%96%B0LP%20%E5%8D%B8%E9%9B%BB%E5%8A%9B%20%E3%82%A2%E3%82%A4%E3%82%B3%E3%83%B3%20.png"
                    alt="卸電力市場"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-lg font-bold text-gray-900">卸電力市場</h3>
              </div>
              <div className="text-center">
                <div className="w-32 h-32 flex items-center justify-center mx-auto mb-4">
                  <img 
                    src="https://grid-scale-battery.eins-ltd.com/hubfs/%E6%A0%AA%E5%BC%8F%E4%BC%9A%E7%A4%BEEINS%20%E7%B3%BB%E7%B5%B1%E7%94%A8%E8%93%84%E9%9B%BB%E6%B1%A0%E4%BA%8B%E6%A5%AD%20%E6%96%B0LP%20%E9%9C%80%E7%B5%A6%E8%AA%BF%E6%95%B4%E5%B8%82%E5%A0%B4%20%E3%82%A2%E3%82%A4%E3%82%B3%E3%83%B3%20.png"
                    alt="需給調整市場"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-lg font-bold text-gray-900">需給調整市場</h3>
              </div>
            </div>

            <div className="text-center">
              <p className="text-lg font-bold text-gray-900">
                複数の市場から収益を得る構造こそが<br />本モデルの強みです
              </p>
            </div>
          </div>
        </section>

        {/* Why EINS Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                EINSが選ばれる理由
              </h2>
              <p className="text-base text-gray-600 mb-2">—————</p>
              <p className="text-base text-gray-600">一括サポート・税理士監修・市場データに基づいた設計<br />はじめての方も安心して取り組めます</p>
            </div>

            <div className="max-w-5xl mx-auto">
              <img 
                src="https://grid-scale-battery.eins-ltd.com/hs-fs/hubfs/%E6%A0%AA%E5%BC%8F%E4%BC%9A%E7%A4%BEEINS%20%E7%B3%BB%E7%B5%B1%E7%94%A8%E8%93%84%E9%9B%BB%E6%B1%A0%E4%BA%8B%E6%A5%AD%20%E6%96%B0LP%20EINS%E3%81%8C%E9%81%B8%E3%81%B0%E3%82%8C%E3%82%8B%E7%90%86%E7%94%B1-1.png"
                alt="EINSが選ばれる理由"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </section>

        {/* Sold Out Banner */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-8">
              <p className="text-xl font-bold text-gray-900 mb-2">
                \🏅完売実績が信頼の証です/
              </p>
              <p className="text-lg font-bold text-gray-900 mb-2">
                第1期・第2期 共有型モデル
              </p>
              <p className="text-2xl font-bold text-red-600">
                完売御礼
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <img 
                src="https://grid-scale-battery.eins-ltd.com/hs-fs/hubfs/%E6%A0%AA%E5%BC%8F%E4%BC%9A%E7%A4%BEEINS%20%E7%B3%BB%E7%B5%B1%E7%94%A8%E8%93%84%E9%9B%BB%E6%B1%A0%E4%BA%8B%E6%A5%AD%20%E6%96%B0LP%20%E8%93%84%E9%9B%BB%E6%B1%A0%E5%AE%8C%E5%A3%B2.png"
                alt="完売御礼"
                className="w-full h-auto"
              />
            </div>
          </div>
        </section>

        {/* Recruitment Banner */}
        <section className="py-8 bg-gradient-to-r from-teal-600 to-blue-600">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center">
              <img 
                src="https://grid-scale-battery.eins-ltd.com/hubfs/LP%20%E3%83%90%E3%83%8A%E3%83%BC%20%E3%82%AA%E3%83%BC%E3%83%8A%E3%83%BC%E5%8B%9F%E9%9B%86%20PC.png"
                alt="オーナー募集"
                className="w-full h-auto max-w-4xl mx-auto"
              />
            </div>
          </div>
        </section>

        {/* Simulation Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                純利益から回収期間まで丸わかり!
              </h2>
              <p className="text-base text-gray-600 mb-2">
                税務・会計のプロが専門知識を交えて監修した
              </p>
              <p className="text-base text-gray-600">
                精度の高いシミュレーションでより現実的な収支予測が可能!
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <img 
                src="https://grid-scale-battery.eins-ltd.com/hubfs/%E6%A0%AA%E5%BC%8F%E4%BC%9A%E7%A4%BEEINS%20%E7%B3%BB%E7%B5%B1%E7%94%A8%E8%93%84%E9%9B%BB%E6%B1%A0%E4%BA%8B%E6%A5%AD%20%E6%96%B0LP%20%E5%8F%8E%E6%94%AF%E3%82%B7%E3%83%9F%E3%83%A5%E3%83%AC%E3%83%BC%E3%82%B7%E3%83%A7%E3%83%B3.png"
                alt="収支シミュレーション"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>

            <div className="text-center mt-8">
              <p className="text-base font-bold text-gray-900 mb-4">
                蓄電池ビジネスに興味をお持ちの方へ<br />詳細なご案内をご用意しています
              </p>
              <a 
                href="#contact"
                className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-12 py-4 rounded-lg text-lg font-bold transition-all whitespace-nowrap cursor-pointer"
              >
                収益シミュレーションを見る▶▶
              </a>
            </div>
          </div>
        </section>

        {/* Business Model Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                ビジネスモデルの仕組み
              </h2>
              <p className="text-base text-gray-600 mb-2">—————</p>
              <p className="text-base text-gray-600">導入から運用まで すべてお任せのフルパッケージ<br />設置から運用・保守管理までをワンストップで対応</p>
            </div>

            <div className="max-w-5xl mx-auto mb-12">
              <img 
                src="https://grid-scale-battery.eins-ltd.com/hs-fs/hubfs/%E6%A0%AA%E5%BC%8F%E4%BC%9A%E7%A4%BEEINS%20%E7%B3%BB%E7%B5%B1%E7%94%A8%E8%93%84%E9%9B%BB%E6%B1%A0%E4%BA%8B%E6%A5%AD%20%E6%96%B0LP%20%E3%83%93%E3%82%B8%E3%83%8D%E3%82%B9%E3%81%AE%E4%BB%95%E7%B5%84%E3%81%BF.png"
                alt="ビジネスモデルの仕組み"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>

            <div className="text-center">
              <p className="text-lg font-bold text-gray-900">
                無理なく始められるスキームだからこそ<br />多くの法人に選ばれています
              </p>
            </div>
          </div>
        </section>

        {/* Schedule Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                運用開始までの流れ
              </h2>
              <p className="text-base text-gray-600 mb-2">—————</p>
              <p className="text-base text-gray-600">初めての方も安心!<br />スムーズに運用開始までサポートします</p>
            </div>

            <div className="max-w-5xl mx-auto">
              <img 
                src="https://grid-scale-battery.eins-ltd.com/hs-fs/hubfs/%E6%A0%AA%E5%BC%8F%E4%BC%9A%E7%A4%BEEINS%20%E7%B3%BB%E7%B5%B1%E7%94%A8%E8%93%84%E9%9B%BB%E6%B1%A0%E4%BA%8B%E6%A5%AD%20%E6%96%B0LP%20%E5%B0%8E%E5%85%A5%E3%82%B9%E3%82%B1%E3%82%B8%E3%83%A5%E3%83%BC%E3%83%AB.png"
                alt="導入スケジュール"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </section>

        {/* Notice Section */}
        <section className="py-8 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <img 
                src="https://grid-scale-battery.eins-ltd.com/hs-fs/hubfs/%E6%A0%AA%E5%BC%8F%E4%BC%9A%E7%A4%BEEINS%20%E7%B3%BB%E7%B5%B1%E7%94%A8%E8%93%84%E9%9B%BB%E6%B1%A0%E4%BA%8B%E6%A5%AD%20%E6%96%B0LP%20%E6%B3%A8%E6%84%8F%E4%BA%8B%E9%A0%85-1.png"
                alt="注意事項"
                className="w-full h-auto"
              />
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                よくある質問
              </h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  q: '収益分配はどのように行われますか?',
                  a: '蓄電所全体の市場収益からアグリゲーター手数料・分配手数料・各経費を控除し、管理会社から共有持分割合に応じて収益が分配されます。'
                },
                {
                  q: 'リスク管理はどのように実施されますか?',
                  a: '自然災害のリスクを最小限に抑えるため、ハザードマップでリスクの低いエリアを選定して用地をご提案しています。さらに、万が一の災害に備えて損害補償・休業補償に対応した保険プランにも加入しております。また、設置する設備にはメーカーの製品保証も付帯しており、安心してご利用いただけます。'
                },
                {
                  q: 'メンテナンスや運用管理はどのように行われますか?',
                  a: '定期点検は、蓄電池メーカーおよび施工業者が行い、法定点検に関しては外部の主任技術者に委託します。'
                },
                {
                  q: '蓄電池の寿命はどのくらいですか?',
                  a: '一般的に蓄電池の寿命は約15~20年とされています。充放電回数や設置エリアの気候により多少左右されることがあります。'
                },
                {
                  q: '契約の途中で売却することは可能ですか?',
                  a: '事業譲渡は可能ですが、アグリゲーターやメーカー保証の契約期間中の場合、事前承諾が必要となります。また、将来的な中古市場の動向については現時点では予測が難しい状況です。'
                }
              ].map((faq, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                    <i className={`ri-${openFaq === index ? 'subtract' : 'add'}-line text-2xl text-teal-600 flex-shrink-0`}></i>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'}`}>
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                      <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section id="contact" className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                お問合せフォーム
              </h2>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <form data-readdy-form data-email-subject="【系統用蓄電池投資】お問い合わせ - 株式会社マック" onSubmit={handleFormSubmit} className="space-y-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-bold text-gray-900 mb-2">
                    会社名 <span className="text-red-600">*</span>
                  </label>
                  <input 
                    type="text" 
                    id="company"
                    name="company"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="position" className="block text-sm font-medium text-gray-900 mb-2">
                    役職
                  </label>
                  <select 
                    id="position"
                    name="position"
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 text-sm cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="経営者・代表取締役">経営者・代表取締役</option>
                    <option value="取締役・執行役員">取締役・執行役員</option>
                    <option value="部長・マネージャー">部長・マネージャー</option>
                    <option value="課長・リーダー">課長・リーダー</option>
                    <option value="一般社員・担当者">一般社員・担当者</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="lastname" className="block text-sm font-bold text-gray-900 mb-2">
                      姓 <span className="text-red-600">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="lastname"
                      name="lastname"
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="firstname" className="block text-sm font-bold text-gray-900 mb-2">
                      名 <span className="text-red-600">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="firstname"
                      name="firstname"
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-bold text-gray-900 mb-2">
                    電話番号 <span className="text-red-600">*</span>
                  </label>
                  <input 
                    type="tel" 
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-gray-900 mb-2">
                    メールアドレス <span className="text-red-600">*</span>
                  </label>
                  <input 
                    type="email" 
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="interview" className="block text-sm font-bold text-gray-900 mb-2">
                    オンライン面談の希望 <span className="text-red-600">*</span>
                  </label>
                  <select 
                    id="interview"
                    name="interview"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 text-sm cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="希望する">希望する</option>
                    <option value="希望しない">希望しない</option>
                    <option value="検討中">検討中</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-3">
                    お問合せ項目 <span className="text-red-600">*</span>
                  </label>
                  <div className="space-y-2">
                    {[
                      '系統用蓄電池事業の概要について知りたい',
                      '新規事業として検討したい',
                      '利回りの良い投資先を探している',
                      '事業用地や業務提携に関するご相談',
                      'その他'
                    ].map((item, index) => (
                      <label key={index} className="flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          name="inquiry"
                          value={item}
                          className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-600 cursor-pointer"
                        />
                        <span className="ml-3 text-sm text-gray-700">{item}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold text-gray-900 mb-2">
                    その他を選んだ方はご記入ください
                  </label>
                  <textarea 
                    id="message"
                    name="message"
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 resize-none text-sm"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">最大500文字</p>
                </div>

                <div>
                  <label className="flex items-start cursor-pointer">
                    <input 
                      type="checkbox" 
                      name="consent"
                      required
                      className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-600 mt-1 cursor-pointer flex-shrink-0"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      ※本サービスは法人向けです。個人のお客様へのご案内は行っておりませんので、予めご了承ください。<span className="text-red-600">*</span>
                    </span>
                  </label>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    株式会社EINSはプライバシー保護に努めており、ご提供いただいた情報はサービスのご案内と管理目的のみに使用します。送信をもって
                    <a href="https://eins-ltd.com/privacypolicy/" target="_blank" rel="noopener" className="text-teal-600 hover:underline">プライバシーポリシー</a>
                    にご同意いただいたものといたします。
                  </p>
                </div>

                <button 
                  type="submit"
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg text-base font-bold transition-all whitespace-nowrap cursor-pointer"
                >
                  送信
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
              <div>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg mr-3">
                    <i className="ri-battery-charge-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <div className="text-xl font-bold">株式会社マック</div>
                    <div className="text-xs text-gray-400">系統用蓄電池事業のご案内</div>
                  </div>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  系統用蓄電池事業を専門とする会社です。
                </p>
              </div>

              <div>
                <h3 className="text-lg font-bold mb-4">お問い合わせ</h3>
                <p className="text-gray-400 text-sm mb-2">電話：048-729-7222</p>
                <p className="text-gray-400 text-sm mb-2">営業時間：平日 9:00-18:00</p>
                <p className="text-gray-400 text-sm mb-2">お問い合わせ：info@eins-ltd.com</p>
                <div className="flex space-x-3 mt-4">
                  <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 hover:bg-teal-600 rounded-lg transition-colors group cursor-pointer">
                    <i className="ri-instagram-line text-gray-400 group-hover:text-white text-lg transition-colors"></i>
                  </a>
                  <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 hover:bg-teal-600 rounded-lg transition-colors group cursor-pointer">
                    <i className="ri-line-fill text-gray-400 group-hover:text-white text-lg transition-colors"></i>
                  </a>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-bold mb-4">サイトメニュー</h3>
                <div className="space-y-2">
                  <a href="#recommend" className="block text-gray-400 text-sm hover:text-white cursor-pointer">おすすめ</a>
                  <a href="#market" className="block text-gray-400 text-sm hover:text-white cursor-pointer">市場動向</a>
                  <a href="#reasons" className="block text-gray-400 text-sm hover:text-white cursor-pointer">選ばれる理由</a>
                  <a href="#simulation" className="block text-gray-400 text-sm hover:text-white cursor-pointer">シミュレーション</a>
                  <a href="#faq" className="block text-gray-400 text-sm hover:text-white cursor-pointer">よくある質問</a>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-bold mb-4">サイト情報</h3>
                <p className="text-gray-400 text-sm mb-2">サイト運営：株式会社マック</p>
                <p className="text-gray-400 text-sm mb-2">所在地：〒275−0023<br />埼玉県さいたま市大宮区吉敷町1−135</p>
                <p className="text-gray-400 text-sm mb-2">サイトURL：https://grid-scale-battery.eins-ltd.com</p>
                <p className="text-gray-400 text-sm">サイトマップ：https://grid-scale-battery.eins-ltd.com/sitemap.html</p>
              </div>
            </div>

            <div className="border-t border-gray-800 pt-8 text-center">
              <p className="text-gray-400 text-sm">
                Copyright ©株式会社マック 2025
              </p>
            </div>
          </div>
        </footer>
      </div>

      {/* Popup Banner */}
      <PopupBanner />
    </>
  );
}
